import * as React from "react";
import { useState, useEffect } from "react";
import {
  Stack,
  MessageBar,
  MessageBarType,
  Pivot,
  PivotItem,
  Spinner,
  SpinnerSize,
  mergeStyles,
  getTheme,
} from "@fluentui/react";
import { apiBaseUrl, isAuthConfigured } from "../../auth/authConfig";
import { signIn, signOut, getUser, isSignedIn, authHeaders, AuthUser } from "../../auth/authDialog";
import { refreshSheetyBanksFormulas } from "../refreshFunctions";
import { setMapOpenBankHandler } from "../mapDialog";
import {
  BankInfo,
  Entitlement,
  SearchState,
  ScreenerState,
  initialScreenerState,
  MarketsState,
  initialMarketsState,
  TopTab,
  SUB_TABS,
} from "./types";
import Header from "./Header";
import SideNav from "./SideNav";
import CompanySearch from "./pages/CompanySearch";
import CompanyScreener from "./pages/CompanyScreener";
import CompanyInfo from "./pages/CompanyInfo";
import Markets from "./pages/Markets";
import Snapshot from "./pages/Snapshot";
import HelpGettingStarted from "./pages/HelpGettingStarted";
import HelpGlossary from "./pages/HelpGlossary";

const API_URL = apiBaseUrl();

const theme = getTheme();

const containerStyle = mergeStyles({
  height: "100vh",
  display: "flex",
  flexDirection: "column",
});

const bodyStyle = mergeStyles({
  flex: 1,
  minHeight: 0,
  display: "flex",
});

const contentStyle = mergeStyles({
  flex: 1,
  minWidth: 0,
  padding: "12px",
  overflow: "auto",
});

const pivotStyles = {
  root: {
    borderBottom: `1px solid ${theme.palette.neutralLight}`,
    backgroundColor: theme.palette.neutralLighterAlt,
  },
  link: { height: 32, lineHeight: 32 },
  linkIsSelected: { height: 32, lineHeight: 32 },
};

export interface TaskPaneProps {}

const TaskPane: React.FunctionComponent<TaskPaneProps> = () => {
  // Navigation — Company → Search is the landing page.
  const [topTab, setTopTab] = useState<TopTab>("company");
  const [subTab, setSubTab] = useState<Record<TopTab, string>>({ company: "search", help: "start" });

  // Shared between Company pages: the bank picked on the Search tab, plus the
  // search query/results so the list is still there after viewing Info.
  const [selectedBank, setSelectedBank] = useState<BankInfo | null>(null);
  const [search, setSearch] = useState<SearchState>({
    query: "",
    searchedQuery: null,
    results: [],
  });
  const [screener, setScreener] = useState<ScreenerState>(initialScreenerState);
  const [markets, setMarkets] = useState<MarketsState>(initialMarketsState);

  const [user, setUser] = useState<AuthUser | null>(null);
  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [entitlement, setEntitlement] = useState<Entitlement | null>(null);

  const [refreshing, setRefreshing] = useState(false);
  const [refreshNote, setRefreshNote] = useState<string | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);

  const navigate = (top: TopTab, sub: string) => {
    setTopTab(top);
    setSubTab((prev) => ({ ...prev, [top]: sub }));
  };

  const handleRefresh = async () => {
    setRefreshError(null);
    setRefreshNote(null);
    setRefreshing(true);
    try {
      const result = await refreshSheetyBanksFormulas();
      setRefreshNote(
        !result.scoped
          ? "Recalculated the whole workbook (this Excel version can't scope the refresh)."
          : result.count === 0
            ? "No SHEETYBANKS formulas found in this workbook."
            : `Refreshed ${result.count} SHEETYBANKS formula${result.count === 1 ? "" : "s"}.`
      );
      setTimeout(() => setRefreshNote(null), 5000);
    } catch (err) {
      setRefreshError(`Refresh failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setRefreshing(false);
    }
  };

  // Fetch the signed-in user's subscription tier/status from the API. /me is authed
  // but not subscription-gated, so this works even for an unsubscribed user.
  const refreshEntitlement = async () => {
    try {
      const res = await fetch(`${API_URL}/me`, { headers: authHeaders() });
      if (!res.ok) return;
      const data = (await res.json()) as Entitlement;
      setEntitlement(data);
    } catch {
      // Non-fatal — the gate still enforces server-side; this is display only.
    }
  };

  // Reflect any token already cached from a previous session.
  useEffect(() => {
    if (isSignedIn()) {
      setUser(getUser());
      refreshEntitlement();
    }
  }, []);

  const handleSignIn = async () => {
    setAuthError(null);
    setAuthBusy(true);
    try {
      const signedIn = await signIn();
      setUser(signedIn);
      await refreshEntitlement();
    } catch (err) {
      setAuthError(`Sign-in failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setAuthBusy(false);
    }
  };

  const handleSignOut = async () => {
    // Clear the UI immediately; the dialog ends the Auth0 session in the background.
    setUser(null);
    setEntitlement(null);
    setAuthBusy(true);
    try {
      await signOut();
    } catch {
      // Local token is already cleared; a failed remote logout is non-fatal.
    } finally {
      setAuthBusy(false);
    }
  };

  const subscriptionBlocked =
    user != null && entitlement != null && !["active", "trialing"].includes(entitlement.status);

  const selectBank = (bank: BankInfo) => {
    setSelectedBank(bank);
    navigate("company", "info");
  };

  // "Open in task pane" from the market map: look up the bank's city/state
  // for the Info card, falling back to what the map knows if that fails.
  useEffect(() => {
    setMapOpenBankHandler(async ({ cert, name }) => {
      let bank: BankInfo = { cert, name: name ?? `Cert ${cert}`, city: null, state: null };
      try {
        const res = await fetch(`${API_URL}/banks/${cert}`, { headers: authHeaders() });
        if (res.ok) {
          const detail = (await res.json()) as BankInfo;
          bank = { cert, name: detail.name, city: detail.city, state: detail.state };
        }
      } catch {
        // Keep the fallback; the Info page loads its own details anyway.
      }
      selectBank(bank);
    });
    return () => setMapOpenBankHandler(null);
  }, []);

  const renderPage = () => {
    if (topTab === "company") {
      if (subTab.company === "search") {
        return (
          <CompanySearch
            search={search}
            onSearchChange={setSearch}
            selectedCert={selectedBank?.cert ?? null}
            onSelect={selectBank}
          />
        );
      }
      if (subTab.company === "screener") {
        return (
          <CompanyScreener
            screener={screener}
            onScreenerChange={setScreener}
            selectedCert={selectedBank?.cert ?? null}
            onSelect={selectBank}
          />
        );
      }
      if (subTab.company === "markets") {
        return <Markets markets={markets} onMarketsChange={setMarkets} />;
      }
      if (subTab.company === "snapshot") {
        return <Snapshot />;
      }
      return <CompanyInfo bank={selectedBank} onGoToSearch={() => navigate("company", "search")} />;
    }
    return subTab.help === "start" ? <HelpGettingStarted /> : <HelpGlossary />;
  };

  return (
    <div className={containerStyle}>
      <Header
        user={user}
        entitlement={entitlement}
        authBusy={authBusy}
        refreshing={refreshing}
        onSignIn={handleSignIn}
        onSignOut={handleSignOut}
        onRefresh={handleRefresh}
      />

      <Pivot
        selectedKey={topTab}
        onLinkClick={(item) => item && setTopTab(item.props.itemKey as TopTab)}
        headersOnly
        styles={pivotStyles}
      >
        <PivotItem headerText="Company" itemKey="company" />
        <PivotItem headerText="Help" itemKey="help" />
      </Pivot>

      <div className={bodyStyle}>
        <SideNav
          tabs={SUB_TABS[topTab]}
          selectedKey={subTab[topTab]}
          onSelect={(key) => setSubTab((prev) => ({ ...prev, [topTab]: key }))}
        />
        <div className={contentStyle}>
          <Stack tokens={{ childrenGap: 8 }}>
            {!isAuthConfigured() && (
              <MessageBar messageBarType={MessageBarType.warning}>
                Auth0 is not configured yet. Fill in <code>src/auth/authConfig.ts</code> to enable
                sign-in.
              </MessageBar>
            )}
            {authBusy && <Spinner size={SpinnerSize.small} label="Opening sign-in…" />}
            {authError && (
              <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setAuthError(null)}>
                {authError}
              </MessageBar>
            )}
            {subscriptionBlocked && (
              <MessageBar messageBarType={MessageBarType.warning}>
                No active subscription (status: {entitlement?.status}) — data is locked.
              </MessageBar>
            )}
            {refreshError && (
              <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setRefreshError(null)}>
                {refreshError}
              </MessageBar>
            )}
            {refreshNote && (
              <MessageBar messageBarType={MessageBarType.info} onDismiss={() => setRefreshNote(null)}>
                {refreshNote}
              </MessageBar>
            )}
            {renderPage()}
          </Stack>
        </div>
      </div>
    </div>
  );
};

export default TaskPane;
